import React, { JSX, useContext } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from 'react-router-dom';

import HomePage from '../pages/HomePage';
import LoginPage from '../pages/LoginPage';
import CreateRecordPage from '../pages/CreateRecordPage';
import RecordDetailPage from '../pages/RecordDetailPage';
import DashboardPage from '../pages/DashboardPage';
import DraftsPage from '../pages/DraftsPage';

import Navbar from '../components/Navbar';
import LoadingPage from '../components/Loading';
import { AuthContext } from '../context/AuthContext';

// A simple wrapper to protect routes

const PrivateRoute: React.FC<{
    children: JSX.Element;
    isLoggedIn: boolean;
    loading: boolean;
}> = ({ children, isLoggedIn, loading }) => {
    const location = useLocation();
    if (!loading && !isLoggedIn) {
        const redirect = encodeURIComponent(
            location.pathname + location.search
        );
        return <Navigate to={`/login?redirect=${redirect}`} replace />;
    }
    if (!loading && isLoggedIn) {
        return children;
    }
    if (loading) {
        return <LoadingPage />;
    }
    return null; // Fallback in case none of the conditions match
};

const AppRoutes: React.FC = () => {
    const { isLoggedIn, loading } = useContext(AuthContext);
    return (
        <BrowserRouter>
            <Navbar />
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes */}
                <Route
                    path="/record-editor/:triplet_id?"
                    element={
                        <PrivateRoute
                            isLoggedIn={isLoggedIn()}
                            loading={loading}
                        >
                            <CreateRecordPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/drafts"
                    element={
                        <PrivateRoute
                            isLoggedIn={isLoggedIn()}
                            loading={loading}
                        >
                            <DraftsPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/records/:id"
                    element={
                        <PrivateRoute
                            isLoggedIn={isLoggedIn()}
                            loading={loading}
                        >
                            <RecordDetailPage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/"
                    element={
                        <PrivateRoute
                            isLoggedIn={isLoggedIn()}
                            loading={loading}
                        >
                            <HomePage />
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/dashboard"
                    element={
                        <PrivateRoute
                            isLoggedIn={isLoggedIn()}
                            loading={loading}
                        >
                            <DashboardPage />
                        </PrivateRoute>
                    }
                />

                {/* Fallback to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
};

export default AppRoutes;
